/**
 * AYU Community Chat
 * Gruppen-Chat für die AYU-Community
 * Design: Sacred Modern — Warm Ivory + Gold-Akzente
 * Features: Text + Bild-Upload + Edit/Delete eigener Nachrichten
 * Mobile-optimiert: Scrollbare Tabs, kein Overflow
 */

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { trpc } from "@/lib/trpc";
import { useClientAuth } from "@/contexts/ClientAuthContext";
import {
  Users,
  Send,
  MessageSquare,
  Loader2,
  Hash,
  ImagePlus,
  X,
  CheckCircle2,
  ExternalLink,
  Pencil,
  Trash2,
  Check,
  MoreVertical,
  ArrowDown,
  ChevronDown,
} from "lucide-react";

import { createLogger } from "@/lib/logger";
import { useI18n } from "@/contexts/I18nContext";
const log = createLogger("Appcommunity");

export default function AppCommunity() {
  const { t } = useI18n();
  const { client } = useClientAuth();
  const [activeRoom, setActiveRoom] = useState("allgemein");
  const [message, setMessage] = useState("");
  const [pendingImage, setPendingImage] = useState<{ base64: string; mimeType: string; preview: string } | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Edit/Delete state
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState("");
  const [menuOpenId, setMenuOpenId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  const { data: rooms, isLoading: roomsLoading } = trpc.community.getRooms.useQuery();

  const { data: chatData, isLoading: messagesLoading } = trpc.community.getMessages.useQuery(
    { roomSlug: activeRoom, limit: 50 },
    { refetchInterval: 5000 }
  );

  // Reset scroll state when switching rooms
  useEffect(() => {
    isInitialLoadRef.current = true;
    prevMessageCountRef.current = 0;
  }, [activeRoom]);

  // Batch-fetch avatars for all unique senders
  const senderIds = useMemo(() => {
    if (!chatData?.messages) return [];
    const ids = new Set<string>();
    for (const m of chatData.messages) {
      if (m.senderClientId && m.senderClientId !== "admin" && m.senderClientId !== "ayu-bot") {
        ids.add(m.senderClientId);
      }
    }
    return Array.from(ids);
  }, [chatData?.messages]);

  const { data: avatarsData } = trpc.clientAuth.getAvatarsBatch.useQuery(
    { clientIds: senderIds },
    { enabled: senderIds.length > 0, staleTime: 60_000 }
  );
  const avatarMap = avatarsData?.avatars ?? {};

  const utils = trpc.useUtils();

  const sendMutation = trpc.community.sendMessage.useMutation({
    onSuccess: () => {
      setMessage("");
      // Reset textarea height after sending
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
      }
      utils.community.getMessages.invalidate({ roomSlug: activeRoom });
    },
  });

  const editMutation = trpc.community.editMessage.useMutation({
    onSuccess: () => {
      setEditingId(null);
      setEditText("");
      utils.community.getMessages.invalidate({ roomSlug: activeRoom });
    },
  });

  const deleteMutation = trpc.community.deleteMessage.useMutation({
    onSuccess: () => {
      setDeletingId(null);
      utils.community.getMessages.invalidate({ roomSlug: activeRoom });
    },
  });

  const uploadImageMutation = trpc.community.uploadImage.useMutation();

  // Close menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpenId(null);
      }
    };
    if (menuOpenId !== null) {
      document.addEventListener("mousedown", handler);
    }
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpenId]);

  // Focus edit input
  useEffect(() => {
    if (editingId !== null && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingId]);

  // Smart auto-scroll: only scroll to bottom when user is already near the bottom
  // or when the user just sent a message themselves
  const prevMessageCountRef = useRef(0);
  const isInitialLoadRef = useRef(true);
  const userSentRef = useRef(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [newMessageCount, setNewMessageCount] = useState(0);
  const [showNewMessageIndicator, setShowNewMessageIndicator] = useState(false);

  // Check if user is scrolled near the bottom (within 150px)
  const isNearBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return true;
    const threshold = 150;
    return container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
  }, []);

  // Track scroll position to hide indicator when user scrolls to bottom
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      if (isNearBottom()) {
        setShowNewMessageIndicator(false);
        setNewMessageCount(0);
      }
    };
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [isNearBottom]);

  useEffect(() => {
    const currentCount = chatData?.messages?.length ?? 0;
    const prevCount = prevMessageCountRef.current;

    // Scroll on initial load (jump, no animation)
    if (isInitialLoadRef.current && currentCount > 0) {
      isInitialLoadRef.current = false;
      prevMessageCountRef.current = currentCount;
      // Use requestAnimationFrame to ensure DOM is rendered
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
      });
      return;
    }

    // New messages arrived
    if (currentCount > prevCount) {
      const newCount = currentCount - prevCount;
      // Always scroll if user just sent a message
      if (userSentRef.current) {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        userSentRef.current = false;
        setShowNewMessageIndicator(false);
        setNewMessageCount(0);
      }
      // Also scroll if user was already near the bottom (reading latest messages)
      else if (isNearBottom()) {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        setShowNewMessageIndicator(false);
        setNewMessageCount(0);
      }
      // Otherwise: user is reading older messages — show indicator
      else {
        setNewMessageCount((prev) => prev + newCount);
        setShowNewMessageIndicator(true);
      }
    }

    prevMessageCountRef.current = currentCount;
  }, [chatData?.messages, isNearBottom]);

  // Scroll to bottom when clicking the new messages indicator
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    setShowNewMessageIndicator(false);
    setNewMessageCount(0);
  }, []);

  const handleSend = useCallback(async () => {
    if ((!message.trim() && !pendingImage) || !client) return;

    let uploadedImageUrl: string | undefined;

    if (pendingImage) {
      setImageUploading(true);
      try {
        const uploadResult = await uploadImageMutation.mutateAsync({
          imageBase64: pendingImage.base64,
          mimeType: pendingImage.mimeType,
          clientId: client.clientId,
        });
        if (uploadResult.success && uploadResult.url) {
          uploadedImageUrl = uploadResult.url;
        }
      } catch (err) {
        log.error("Image upload error:");
      } finally {
        setImageUploading(false);
        setPendingImage(null);
      }
    }

    userSentRef.current = true;
    sendMutation.mutate({
      roomSlug: activeRoom,
      clientId: client.clientId,
      senderName: client.firstName,
      content: message.trim() || (uploadedImageUrl ? "[Bild]" : ""),
      imageUrl: uploadedImageUrl,
    });
  }, [message, pendingImage, client, activeRoom, sendMutation, uploadImageMutation]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleImageSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return;
    if (file.size > 10 * 1024 * 1024) return;

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = (reader.result as string).split(",")[1];
      const preview = reader.result as string;
      setPendingImage({ base64, mimeType: file.type, preview });
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  }, []);

  const handleStartEdit = useCallback((msgId: number, content: string) => {
    // Extract text only (not image tags)
    const textOnly = content.replace(/\[IMG\].*?\[\/IMG\]/g, "").trim();
    setEditingId(msgId);
    setEditText(textOnly);
    setMenuOpenId(null);
  }, []);

  const handleConfirmEdit = useCallback(() => {
    if (!editingId || !editText.trim() || !client) return;
    editMutation.mutate({
      messageId: editingId,
      clientId: client.clientId,
      newContent: editText.trim(),
    });
  }, [editingId, editText, client, editMutation]);

  const handleCancelEdit = useCallback(() => {
    setEditingId(null);
    setEditText("");
  }, []);

  const handleDelete = useCallback((msgId: number) => {
    setDeletingId(msgId);
    setMenuOpenId(null);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!deletingId || !client) return;
    deleteMutation.mutate({
      messageId: deletingId,
      clientId: client.clientId,
    });
  }, [deletingId, client, deleteMutation]);

  function parseImageContent(content: string): { imageUrl: string | null; text: string } {
    const imgMatch = content.match(/\[IMG\](.*?)\[\/IMG\]/);
    if (imgMatch) {
      const imageUrl = imgMatch[1];
      const text = content.replace(imgMatch[0], "").trim();
      return { imageUrl, text };
    }
    return { imageUrl: null, text: content };
  }

  const groupedMessages = useMemo(() => {
    if (!chatData?.messages) return [];

    type Msg = (typeof chatData.messages)[number];
    const groups: Array<{ date: string; messages: Msg[] }> = [];
    let currentDate = "";

    for (const msg of chatData.messages) {
      const msgDate = new Date(msg.createdAt).toLocaleDateString("de-DE", {
        weekday: "long",
        day: "numeric",
        month: "long",
      });

      if (msgDate !== currentDate) {
        currentDate = msgDate;
        groups.push({ date: msgDate, messages: [] });
      }
      groups[groups.length - 1].messages.push(msg);
    }
    return groups;
  }, [chatData?.messages]);

  const activeRoomData = rooms?.find((r) => r.slug === activeRoom);

  // Telegram-Community-Status
  const { data: tgStatus } = trpc.community.getTelegramStatus.useQuery(
    { clientId: client?.clientId ?? "" },
    { enabled: !!client?.clientId }
  );
  const [showTelegramBanner, setShowTelegramBanner] = useState(true);

  return (
    <div className="h-full flex flex-col overflow-hidden px-4 pt-4 pb-2">
      {/* Delete Confirmation Dialog */}
      {deletingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-xl border border-[#d4dcc4] p-5 max-w-sm mx-4 animate-in zoom-in-95 duration-200">
            <h3 className="text-sm font-semibold text-[#2D2D2D] mb-2">{t("community.nachrichtLoeschen")}</h3>
            <p className="text-xs text-[#2D2D2D]/60 mb-4">
              {t("community.unwiderruflich")}
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setDeletingId(null)}
                className="px-3 py-1.5 text-xs font-medium text-[#5a6b4a] bg-[#6b7f4e]/5 hover:bg-[#6b7f4e]/10 rounded-lg transition-colors"
              >
                {t("community.abbrechen")}
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deleteMutation.isPending}
                className="px-3 py-1.5 text-xs font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors flex items-center gap-1.5"
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Trash2 className="w-3 h-3" />
                )}
                {t("community.loeschen")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Telegram Community Banner */}
      {showTelegramBanner && tgStatus && !tgStatus.isLinked && client && (
        <div className="shrink-0 mb-3 bg-gradient-to-r from-[#0088cc]/5 to-[#6b7f4e]/5 border border-[#0088cc]/15 rounded-2xl p-4 animate-in fade-in duration-300">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#6b7f4e]/10 flex items-center justify-center shrink-0">
              <svg className="w-5 h-5 text-[#6b7f4e]" viewBox="0 0 24 24" fill="currentColor">
                <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-medium text-[#2D2D2D] mb-1">{t("community.telegramBeitreten")}</h3>
              <p className="text-xs text-[#2D2D2D]/50 mb-2 leading-relaxed">
                Erhalte Community-Nachrichten auch auf Telegram — in 2 einfachen Schritten:
              </p>
              <div className="bg-white/60 rounded-xl p-3 mb-3 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#6b7f4e] text-white text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">1</span>
                  <p className="text-xs text-[#5a6b4a]">
                    Klicke auf <strong>„Community Bot öffnen“</strong> — Telegram öffnet sich automatisch.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-[#6b7f4e] text-white text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">2</span>
                  <p className="text-xs text-[#5a6b4a]">
                    Tippe auf <span className="font-mono bg-[#f0f3eb] px-1 py-0.5 rounded text-[#2d3a1e] font-medium text-[11px]">START</span> — dein Account wird <strong>automatisch verknüpft</strong>.
                  </p>
                </div>
              </div>
              <a
                href={`https://t.me/Ayu_community_bot?start=ref_${client.clientId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#6b7f4e] text-white text-xs font-medium hover:bg-[#5a6b3e] transition-colors shadow-sm"
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                </svg>
                {t("community.communityBotOeffnen")}
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <button
              onClick={() => setShowTelegramBanner(false)}
              className="text-[#2D2D2D]/20 hover:text-[#2D2D2D]/40 transition-colors shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Telegram verbunden — kompakter Hinweis */}
      {showTelegramBanner && tgStatus?.isLinked && client && (
        <div className="shrink-0 mb-3 flex items-center gap-2 bg-[#6b7f4e]/5 border border-[#6b7f4e]/10 rounded-xl px-3 py-2">
          <CheckCircle2 className="w-4 h-4 text-[#6b7f4e] shrink-0" />
          <span className="text-xs text-[#6b7f4e] font-medium flex-1">
            {t("community.telegramVerbunden")}{tgStatus.telegramUsername ? ` — @${tgStatus.telegramUsername}` : ""}
          </span>
          <a
            href={`https://t.me/Ayu_community_bot?start=ref_${client.clientId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-[#0088cc] hover:underline shrink-0"
          >
            {t("community.botOeffnen")}
          </a>
          <button
            onClick={() => setShowTelegramBanner(false)}
            className="text-[#2D2D2D]/20 hover:text-[#2D2D2D]/40 transition-colors shrink-0"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="shrink-0 pb-3 border-b border-[#2D2D2D]/5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-[#6b7f4e]/10 flex items-center justify-center shrink-0">
            <Users className="w-5 h-5 text-[#6b7f4e]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-[#2D2D2D] font-['Playfair_Display']">
              Community
            </h1>
            <p className="text-xs text-[#2D2D2D]/40 truncate">
              {activeRoomData?.description ?? "Teile Erfahrungen, stelle Fragen und finde Gleichgesinnte auf deiner Gesundheitsreise"}
            </p>
          </div>
        </div>

        {/* Room Tabs — horizontal scrollbar auf Mobile */}
        {rooms && rooms.length > 1 && (
          <div className="overflow-x-auto -mx-1 px-1 scrollbar-hide">
            <div className="flex gap-1 bg-[#2D2D2D]/[0.03] rounded-lg p-1 w-max min-w-full">
              {rooms.map((room) => (
                <button
                  key={room.slug}
                  onClick={() => setActiveRoom(room.slug)}
                  className={`
                    flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap shrink-0
                    ${activeRoom === room.slug
                      ? "bg-white text-[#6b7f4e] shadow-sm"
                      : "text-[#2D2D2D]/40 hover:text-[#2D2D2D]/60"
                    }
                  `}
                >
                  <Hash className="w-3 h-3 shrink-0" />
                  {room.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Messages Area */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto py-4 space-y-4 min-h-0">
        {messagesLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-5 h-5 animate-spin text-[#6b7f4e]" />
          </div>
        ) : chatData?.messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <div className="w-16 h-16 rounded-2xl bg-[#6b7f4e]/10 flex items-center justify-center mb-4">
              <MessageSquare className="w-8 h-8 text-[#6b7f4e]/40" />
            </div>
            <h3 className="text-sm font-medium text-[#2D2D2D]/60 mb-1">
              Noch keine Nachrichten
            </h3>
            <p className="text-xs text-[#2D2D2D]/30 max-w-xs">
              Sei die erste Person, die etwas teilt. Die Community wartet auf dich!
            </p>
          </div>
        ) : (
          groupedMessages.map((group) => (
            <div key={group.date}>
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-[#2D2D2D]/5" />
                <span className="text-[10px] text-[#2D2D2D]/30 uppercase tracking-wider font-medium whitespace-nowrap">
                  {group.date}
                </span>
                <div className="flex-1 h-px bg-[#2D2D2D]/5" />
              </div>

              {group.messages.map((msg) => {
                const isOwn = msg.senderClientId === client?.clientId;
                const isAdmin = client?.isAdmin === true;
                const canDelete = isOwn || isAdmin;
                const isSystem = msg.messageType === "system";
                const initial = msg.senderName[0]?.toUpperCase() ?? "?";
                const time = new Date(msg.createdAt).toLocaleTimeString("de-DE", {
                  hour: "2-digit",
                  minute: "2-digit",
                });
                const isEditing = editingId === msg.id;
                const isMenuOpen = menuOpenId === msg.id;

                if (isSystem) {
                  return (
                    <div key={msg.id} className="text-center py-2">
                      <span className="text-[10px] text-[#2D2D2D]/30 bg-[#2D2D2D]/[0.03] px-3 py-1 rounded-full">
                        {msg.content}
                      </span>
                    </div>
                  );
                }

                const { imageUrl, text } = parseImageContent(msg.content);

                return (
                  <div
                    key={msg.id}
                    className={`flex gap-2.5 group ${isOwn ? "flex-row-reverse" : ""}`}
                  >
                    <div
                      className={`
                        w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-xs font-medium overflow-hidden
                        ${isOwn
                          ? "bg-[#6b7f4e]/10 text-[#6b7f4e]"
                          : "bg-[#6b7f4e]/10 text-[#6b7f4e]"
                        }
                      `}
                    >
                      {avatarMap[msg.senderClientId] ? (
                        <img
                          src={avatarMap[msg.senderClientId]!}
                          alt={msg.senderName}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        initial
                      )}
                    </div>

                    <div className={`max-w-[75%] min-w-0 relative ${isOwn ? "items-end" : "items-start"}`}>
                      {!isOwn && (
                        <p className="text-[10px] text-[#2D2D2D]/40 mb-0.5 ml-1 font-medium truncate">
                          {msg.senderName}
                        </p>
                      )}

                      {isEditing ? (
                        /* Edit Mode */
                        <div className="flex items-center gap-1.5">
                          <input
                            ref={editInputRef}
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleConfirmEdit();
                              if (e.key === "Escape") handleCancelEdit();
                            }}
                            className="flex-1 text-sm bg-white border border-[#6b7f4e]/30 rounded-xl px-3 py-2 focus:outline-none focus:border-[#6b7f4e] focus:ring-1 focus:ring-[#6b7f4e]/20 min-w-0"
                            maxLength={2000}
                          />
                          <button
                            onClick={handleConfirmEdit}
                            disabled={editMutation.isPending || !editText.trim()}
                            className="p-1.5 rounded-lg bg-[#6b7f4e] text-white hover:bg-[#5a6e3f] transition-colors disabled:opacity-30"
                            title="Speichern"
                          >
                            {editMutation.isPending ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Check className="w-3.5 h-3.5" />
                            )}
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="p-1.5 rounded-lg text-[#8a9680] hover:bg-red-50 hover:text-red-500 transition-colors"
                            title="Abbrechen"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        /* Normal Message */
                        <div className="relative">
                          <div
                            className={`
                              px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed break-words
                              ${isOwn
                                ? "bg-[#6b7f4e]/10 text-[#2D2D2D] rounded-br-md"
                                : "bg-white border border-[#2D2D2D]/5 text-[#2D2D2D] rounded-bl-md"
                              }
                            `}
                          >
                            {imageUrl && (
                              <div className="mb-2">
                                <img
                                  src={imageUrl}
                                  alt="Geteiltes Bild"
                                  className="max-w-full max-h-64 rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={() => window.open(imageUrl, "_blank")}
                                />
                              </div>
                            )}
                            {text && text !== "[Bild]" && text}
                          </div>

                          {/* Edit/Delete Menu for own messages + Admin delete for all */}
                          {canDelete && !isEditing && (
                            <div className={`absolute top-0 ${isOwn ? "left-0 -translate-x-full" : "right-0 translate-x-full"} flex items-center`}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMenuOpenId(isMenuOpen ? null : msg.id);
                                }}
                                className="p-1 rounded-md text-[#2D2D2D]/0 group-hover:text-[#2D2D2D]/30 hover:!text-[#2D2D2D]/60 hover:bg-[#2D2D2D]/5 transition-all"
                                title="Optionen"
                              >
                                <MoreVertical className="w-3.5 h-3.5" />
                              </button>

                              {isMenuOpen && (
                                <div
                                  ref={menuRef}
                                  className="absolute top-full right-0 z-50 mt-1 bg-white rounded-xl shadow-lg border border-[#d4dcc4] py-1 min-w-[130px] animate-in fade-in zoom-in-95 duration-150"
                                >
                                  {isOwn && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleStartEdit(msg.id, msg.content);
                                      }}
                                      className="w-full text-left px-3 py-2 text-xs text-[#5a6b4a] hover:bg-[#f0f3eb] flex items-center gap-2 transition-colors"
                                    >
                                      <Pencil className="w-3 h-3" />
                                      Bearbeiten
                                    </button>
                                  )}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDelete(msg.id);
                                    }}
                                    className="w-full text-left px-3 py-2 text-xs text-red-500 hover:bg-red-50 flex items-center gap-2 transition-colors"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                    {isOwn ? "Löschen" : "Löschen (Admin)"}
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      <p className={`text-[10px] text-[#2D2D2D]/25 mt-0.5 ${isOwn ? "text-right mr-1" : "ml-1"}`}>
                        {time}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* New Messages Indicator */}
      {showNewMessageIndicator && (
        <div className="shrink-0 flex justify-center -mt-2 mb-1">
          <button
            onClick={scrollToBottom}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-[#6b7f4e] text-white text-xs font-medium rounded-full shadow-lg shadow-[#6b7f4e]/25 hover:bg-[#5a6e3f] transition-all animate-in slide-in-from-bottom-2 fade-in duration-300"
          >
            <ChevronDown className="w-3.5 h-3.5" />
            {newMessageCount === 1
              ? "1 neue Nachricht"
              : `${newMessageCount} neue Nachrichten`}
          </button>
        </div>
      )}

      {/* Image Preview */}
      {pendingImage && (
        <div className="shrink-0 flex items-center gap-3 bg-[#6b7f4e]/5 border border-[#6b7f4e]/20 rounded-xl px-3 py-2 mb-2 animate-in fade-in duration-300">
          <img src={pendingImage.preview} alt="Vorschau" className="w-12 h-12 rounded-lg object-cover shrink-0" />
          <span className="text-xs text-[#2D2D2D]/60 flex-1 min-w-0 truncate">Bild bereit zum Senden</span>
          {imageUploading && <Loader2 className="w-4 h-4 text-[#6b7f4e] animate-spin shrink-0" />}
          <button onClick={() => setPendingImage(null)} className="text-[#2D2D2D]/30 hover:text-red-500 transition-colors shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Input Area */}
      <div className="shrink-0 pt-3 border-t border-[#2D2D2D]/5">
        <div className="flex items-end gap-2 bg-white rounded-2xl border border-[#2D2D2D]/5 px-3 py-2 shadow-sm">
          <input
            ref={imageInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={handleImageSelect}
          />
          <button
            onClick={() => imageInputRef.current?.click()}
            disabled={sendMutation.isPending || imageUploading}
            className={`
              p-1.5 rounded-lg transition-all shrink-0
              ${pendingImage
                ? "bg-[#6b7f4e]/20 text-[#6b7f4e]"
                : "text-[#2D2D2D]/30 hover:text-[#6b7f4e] hover:bg-[#6b7f4e]/10"
              }
              ${(sendMutation.isPending || imageUploading) ? "opacity-30 cursor-not-allowed" : ""}
            `}
            title="Bild teilen"
          >
            <ImagePlus className="w-4 h-4" />
          </button>

          <textarea
            ref={inputRef}
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              // Auto-resize textarea
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
            onKeyDown={handleKeyDown}
            placeholder={pendingImage ? "Beschreibung..." : "Schreib etwas..."}
            className="flex-1 bg-transparent text-sm text-[#2D2D2D] placeholder:text-[#2D2D2D]/25 outline-none min-w-0 resize-none overflow-y-auto"
            maxLength={2000}
            rows={1}
            style={{ maxHeight: '120px' }}
          />
          <button
            onClick={handleSend}
            disabled={(!message.trim() && !pendingImage) || sendMutation.isPending || imageUploading}
            className={`
              p-2 rounded-xl transition-all shrink-0
              ${(message.trim() || pendingImage)
                ? "bg-[#6b7f4e] text-white hover:bg-[#5a6e3f] shadow-sm"
                : "bg-[#2D2D2D]/[0.03] text-[#2D2D2D]/20"
              }
            `}
          >
            {(sendMutation.isPending || imageUploading) ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </button>
        </div>
        <p className="text-[10px] text-[#2D2D2D]/20 text-center mt-1.5">
          Sei respektvoll und unterstützend.
        </p>
      </div>
    </div>
  );
}
